// Google Analytics Dashboard Component for SEO analysis
import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Alert, Spin, Typography, Row, Col, Tag, message } from 'antd';
import { GoogleOutlined, LineChartOutlined, TrophyOutlined, WarningOutlined, DownloadOutlined } from '@ant-design/icons';
import googleAnalyticsClient from '../utils/googleAnalytics';

const { Title, Text, Paragraph } = Typography;

const GoogleAnalyticsDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [authUrl, setAuthUrl] = useState('');

  useEffect(() => {
    initializeAuth();
    
    // Listen for authentication messages from popup
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'GOOGLE_AUTH_SUCCESS' && event.data.code) {
        completeAuthentication(event.data.code);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Check URL for auth code (fallback method)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code && !isAuthenticated) {
      completeAuthentication(code);
    }
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isAuthenticated]);

  const initializeAuth = async () => {
    setLoading(true);
    try {
      // Try to load stored tokens
      const hasValidTokens = await googleAnalyticsClient.loadStoredTokens();
      setIsAuthenticated(hasValidTokens);
      
      if (!hasValidTokens) {
        // Generate auth URL for new authentication
        const url = googleAnalyticsClient.getAuthUrl();
        setAuthUrl(url);
      } else {
        // Load analytics data if already authenticated
        await loadAnalyticsData();
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setError('Failed to initialize Google Analytics authentication');
    }
    setLoading(false);
  };

  const handleAuthentication = () => {
    if (!authUrl) {
      setError('Authentication URL not available. Please refresh the page.');
      return;
    }

    // Open authentication URL in new window
    const authWindow = window.open(
      authUrl, 
      'google-auth', 
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    // Listen for the authentication callback
    const checkAuth = setInterval(() => {
      try {
        if (authWindow.closed) {
          clearInterval(checkAuth);
          // Check URL parameters for auth code
          const urlParams = new URLSearchParams(window.location.search);
          const code = urlParams.get('code');
          if (code) {
            completeAuthentication(code);
          }
        }
      } catch (error) {
        // Handle cross-origin issues
      }
    }, 1000);
  };

  const completeAuthentication = async (code) => {
    setLoading(true);
    try {
      await googleAnalyticsClient.authenticate(code);
      setIsAuthenticated(true);
      setError(null);
      
      // Clean up URL parameters
      const url = new URL(window.location);
      url.searchParams.delete('code');
      url.searchParams.delete('scope');
      window.history.replaceState({}, document.title, url.toString());
      
      // Load analytics data
      await loadAnalyticsData();
    } catch (error) {
      console.error('Authentication completion failed:', error);
      setError('Authentication failed. Please try again.');
    }
    setLoading(false);
  };

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // LumiBears.com GA4 Property ID
      const propertyId = '514402050';
      
      const [organicData, topPages, seoInsights] = await Promise.all([
        googleAnalyticsClient.getOrganicSearchData(propertyId),
        googleAnalyticsClient.getTopPagesData(propertyId),
        googleAnalyticsClient.generateSEOInsights(propertyId)
      ]);

      setAnalyticsData({
        organicTraffic: organicData,
        topPages: topPages
      });
      setInsights(seoInsights);
      setError(null);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
      setError('Failed to load analytics data. Please check your Google Analytics property ID and permissions.');
    }
    setLoading(false);
  };

  const handleSignOut = () => {
    googleAnalyticsClient.clearAuth();
    setIsAuthenticated(false);
    setAnalyticsData(null);
    setInsights(null);
    setError(null);
    initializeAuth();
  };

  const generateSEOReport = () => {
    if (!analyticsData || !insights) {
      message.error('No data available to export. Please ensure analytics data is loaded.');
      return;
    }

    const currentDate = new Date().toLocaleDateString();
    const reportData = {
      reportGenerated: currentDate,
      reportGeneratedTimestamp: new Date().toISOString(),
      website: 'LumiBears.com',
      timeframe: 'Last 30 days',
      googleAnalyticsPropertyId: '514402050',
      
      // RAW ANALYTICS DATA - LOWEST LEVEL
      rawData: {
        organicTrafficRaw: analyticsData.organicTraffic, // Complete unprocessed data
        topPagesRaw: analyticsData.topPages, // Complete unprocessed data
        insightsRaw: insights, // Complete unprocessed insights
        
        // Individual page records with all available metrics
        allOrganicPages: analyticsData.organicTraffic.map((page, index) => ({
          recordIndex: index,
          sessionSource: page.sessionSource || 'unknown',
          sessionMedium: page.sessionMedium || 'unknown', 
          landingPage: page.landingPage || 'unknown',
          landingPageFull: page.landingPage,
          sessionsRaw: page.sessions,
          sessionsInt: parseInt(page.sessions || 0),
          totalUsersRaw: page.totalUsers,
          totalUsersInt: parseInt(page.totalUsers || 0),
          screenPageViewsRaw: page.screenPageViews,
          screenPageViewsInt: parseInt(page.screenPageViews || 0),
          bounceRateRaw: page.bounceRate,
          bounceRateFloat: parseFloat(page.bounceRate || 0),
          averageSessionDurationRaw: page.averageSessionDuration,
          averageSessionDurationFloat: parseFloat(page.averageSessionDuration || 0),
          averageSessionDurationMinutes: parseFloat(page.averageSessionDuration || 0) / 60,
          
          // All other fields that might be present
          ...Object.keys(page).reduce((acc, key) => {
            if (!['sessionSource', 'sessionMedium', 'landingPage', 'sessions', 'totalUsers', 'screenPageViews', 'bounceRate', 'averageSessionDuration'].includes(key)) {
              acc[`additionalField_${key}`] = page[key];
            }
            return acc;
          }, {})
        })),
        
        allTopPages: analyticsData.topPages.map((page, index) => ({
          recordIndex: index,
          pagePath: page.pagePath || 'unknown',
          pagePathFull: page.pagePath,
          pageTitle: page.pageTitle || 'unknown',
          pageTitleFull: page.pageTitle,
          screenPageViewsRaw: page.screenPageViews,
          screenPageViewsInt: parseInt(page.screenPageViews || 0),
          totalUsersRaw: page.totalUsers,
          totalUsersInt: parseInt(page.totalUsers || 0),
          averageSessionDurationRaw: page.averageSessionDuration,
          averageSessionDurationFloat: parseFloat(page.averageSessionDuration || 0),
          averageSessionDurationMinutes: parseFloat(page.averageSessionDuration || 0) / 60,
          bounceRateRaw: page.bounceRate,
          bounceRateFloat: parseFloat(page.bounceRate || 0),
          
          // All other fields that might be present
          ...Object.keys(page).reduce((acc, key) => {
            if (!['pagePath', 'pageTitle', 'screenPageViews', 'totalUsers', 'averageSessionDuration', 'bounceRate'].includes(key)) {
              acc[`additionalField_${key}`] = page[key];
            }
            return acc;
          }, {})
        }))
      },
      
      // METADATA ABOUT THE DATA
      dataMetadata: {
        organicTrafficRecordCount: analyticsData.organicTraffic.length,
        topPagesRecordCount: analyticsData.topPages.length,
        organicTrafficFields: analyticsData.organicTraffic.length > 0 ? Object.keys(analyticsData.organicTraffic[0]) : [],
        topPagesFields: analyticsData.topPages.length > 0 ? Object.keys(analyticsData.topPages[0]) : [],
        dateRangeQueried: 'Last 30 days',
        apiEndpointsUsed: [
          'https://analyticsdata.googleapis.com/v1beta/properties/514402050:runReport (organic)',
          'https://analyticsdata.googleapis.com/v1beta/properties/514402050:runReport (top pages)'
        ]
      },

      summary: {
        totalOrganicSessions: insights.organicTraffic.totalSessions,
        averageBounceRate: `${insights.organicTraffic.averageBounceRate}%`,
        totalRecommendations: insights.recommendations.length,
        topPagesAnalyzed: analyticsData.topPages.length,
        organicTrafficPages: analyticsData.organicTraffic.length
      },
      keyFindings: {
        topPerformingPage: analyticsData.topPages[0] ? {
          path: analyticsData.topPages[0].pagePath,
          views: analyticsData.topPages[0].screenPageViews,
          users: analyticsData.topPages[0].totalUsers
        } : null,
        bestOrganicLandingPage: analyticsData.organicTraffic[0] ? {
          page: analyticsData.organicTraffic[0].landingPage,
          sessions: analyticsData.organicTraffic[0].sessions,
          bounceRate: `${parseFloat(analyticsData.organicTraffic[0].bounceRate || 0).toFixed(1)}%`
        } : null,
        averageSessionDuration: analyticsData.organicTraffic.length > 0 ? 
          `${(analyticsData.organicTraffic.reduce((sum, page) => sum + parseFloat(page.averageSessionDuration || 0), 0) / analyticsData.organicTraffic.length / 60).toFixed(1)} minutes` : 'N/A'
      },
      seoRecommendations: insights.recommendations.map(rec => ({
        priority: rec.priority,
        issue: rec.message,
        type: rec.type,
        affectedPages: rec.pages ? rec.pages.length : 0
      })),
      organicTrafficAnalysis: analyticsData.organicTraffic.slice(0, 20).map(page => ({
        landingPage: page.landingPage,
        sessions: parseInt(page.sessions || 0),
        users: parseInt(page.totalUsers || 0),
        bounceRate: `${parseFloat(page.bounceRate || 0).toFixed(1)}%`,
        avgSessionDuration: `${(parseFloat(page.averageSessionDuration || 0) / 60).toFixed(1)} min`,
        source: page.sessionSource,
        seoScore: calculatePageSEOScore(page)
      })),
      topPagesAnalysis: analyticsData.topPages.slice(0, 20).map(page => ({
        pagePath: page.pagePath,
        pageViews: parseInt(page.screenPageViews || 0),
        users: parseInt(page.totalUsers || 0),
        avgDuration: `${(parseFloat(page.averageSessionDuration || 0) / 60).toFixed(1)} min`,
        bounceRate: `${parseFloat(page.bounceRate || 0).toFixed(1)}%`,
        engagementScore: calculateEngagementScore(page)
      })),
      actionableInsights: generateActionableInsights(analyticsData, insights),
      nextSteps: generateNextSteps(insights.recommendations)
    };

    return reportData;
  };

  const calculatePageSEOScore = (page) => {
    let score = 50; // Base score
    const bounceRate = parseFloat(page.bounceRate || 0);
    const sessions = parseInt(page.sessions || 0);
    const duration = parseFloat(page.averageSessionDuration || 0);

    // Bounce rate scoring (lower is better)
    if (bounceRate < 40) score += 25;
    else if (bounceRate < 60) score += 15;
    else if (bounceRate < 80) score += 5;
    else score -= 10;

    // Traffic volume scoring
    if (sessions > 100) score += 15;
    else if (sessions > 50) score += 10;
    else if (sessions > 10) score += 5;

    // Engagement duration scoring (higher is better)
    if (duration > 180) score += 10; // 3+ minutes
    else if (duration > 120) score += 5; // 2+ minutes
    else if (duration < 30) score -= 5; // Less than 30 seconds

    return Math.max(0, Math.min(100, score));
  };

  const calculateEngagementScore = (page) => {
    const views = parseInt(page.screenPageViews || 0);
    const users = parseInt(page.totalUsers || 0);
    const duration = parseFloat(page.averageSessionDuration || 0);
    const bounceRate = parseFloat(page.bounceRate || 0);

    let score = 0;
    
    // High traffic gets points
    if (views > 1000) score += 30;
    else if (views > 500) score += 20;
    else if (views > 100) score += 10;

    // Good engagement duration
    if (duration > 120) score += 25;
    else if (duration > 60) score += 15;
    else if (duration > 30) score += 5;

    // Low bounce rate is good
    if (bounceRate < 40) score += 25;
    else if (bounceRate < 60) score += 15;
    else if (bounceRate < 80) score += 5;

    // User retention (views per user ratio)
    const viewsPerUser = users > 0 ? views / users : 1;
    if (viewsPerUser > 2) score += 20;
    else if (viewsPerUser > 1.5) score += 10;

    return Math.min(100, score);
  };

  const generateActionableInsights = (data, insights) => {
    const actionableInsights = [];

    // Traffic insights
    if (insights.organicTraffic.totalSessions < 100) {
      actionableInsights.push({
        category: 'Traffic Growth',
        insight: 'Low organic traffic volume detected',
        recommendation: 'Focus on keyword research and content creation to target more search terms. Consider long-tail keywords related to wellness, emotional support, and children\'s comfort.',
        priority: 'High',
        estimatedImpact: 'Could increase organic traffic by 50-100% in 3-6 months'
      });
    }

    // Bounce rate insights
    const avgBounceRate = parseFloat(insights.organicTraffic.averageBounceRate);
    if (avgBounceRate > 70) {
      actionableInsights.push({
        category: 'User Experience',
        insight: 'High average bounce rate indicates users are leaving quickly',
        recommendation: 'Improve page load speed, add compelling calls-to-action, and ensure content matches search intent. Consider A/B testing different page layouts.',
        priority: 'High',
        estimatedImpact: 'Could reduce bounce rate by 15-25% and improve conversions'
      });
    }

    // Content gaps
    if (data.organicTraffic.length < 10) {
      actionableInsights.push({
        category: 'Content Strategy',
        insight: 'Limited number of pages receiving organic traffic',
        recommendation: 'Create more content around your top-performing keywords. Expand blog content about children\'s emotional wellness, sleep routines, and anxiety management.',
        priority: 'Medium',
        estimatedImpact: 'Could expand organic reach by 40-60%'
      });
    }

    // Technical SEO opportunities
    actionableInsights.push({
      category: 'Technical SEO',
      insight: 'Recent SEO infrastructure improvements implemented',
      recommendation: 'Monitor the impact of new schema markup, internal linking, and meta tag optimizations. Results should show within 4-8 weeks.',
      priority: 'Low',
      estimatedImpact: 'Should see 10-20% improvement in search visibility'
    });

    return actionableInsights;
  };

  const generateNextSteps = (recommendations) => {
    const steps = [
      {
        timeframe: 'This Week',
        actions: [
          'Review and fix any high-priority SEO recommendations',
          'Identify top 5 highest-traffic pages for optimization',
          'Set up regular monitoring schedule for these metrics'
        ]
      },
      {
        timeframe: 'Next 2 Weeks',
        actions: [
          'Create content calendar targeting low-competition keywords',
          'Optimize high-bounce-rate pages with better CTAs and content',
          'Implement A/B tests for key landing pages'
        ]
      },
      {
        timeframe: 'Next Month',
        actions: [
          'Develop comprehensive link-building strategy',
          'Create more pillar content around main product categories',
          'Analyze competitor SEO strategies and identify gaps'
        ]
      },
      {
        timeframe: 'Next Quarter',
        actions: [
          'Evaluate and expand successful content themes',
          'Consider technical improvements based on Core Web Vitals',
          'Plan seasonal content campaigns'
        ]
      }
    ];

    // Add specific recommendations based on current issues
    if (recommendations.length > 0) {
      recommendations.forEach(rec => {
        if (rec.priority === 'high') {
          steps[0].actions.unshift(`ADDRESS URGENT: ${rec.message.split('.')[0]}`);
        }
      });
    }

    return steps;
  };

  const exportToJSON = () => {
    const reportData = generateSEOReport();
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lumibears-seo-report-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('SEO report exported successfully!');
  };

  const exportToCSV = () => {
    const reportData = generateSEOReport();
    
    // Create CSV content
    let csvContent = "LumiBears SEO Analysis Report\n";
    csvContent += `Generated: ${reportData.reportGenerated}\n`;
    csvContent += `Timeframe: ${reportData.timeframe}\n\n`;
    
    csvContent += "SUMMARY\n";
    csvContent += "Metric,Value\n";
    csvContent += `Total Organic Sessions,${reportData.summary.totalOrganicSessions}\n`;
    csvContent += `Average Bounce Rate,${reportData.summary.averageBounceRate}\n`;
    csvContent += `SEO Recommendations,${reportData.summary.totalRecommendations}\n\n`;
    
    csvContent += "TOP ORGANIC TRAFFIC PAGES\n";
    csvContent += "Landing Page,Sessions,Users,Bounce Rate,Avg Duration,SEO Score\n";
    reportData.organicTrafficAnalysis.forEach(page => {
      csvContent += `"${page.landingPage}",${page.sessions},${page.users},${page.bounceRate},${page.avgSessionDuration},${page.seoScore}\n`;
    });
    
    csvContent += "\nTOP PERFORMING PAGES\n";
    csvContent += "Page Path,Page Views,Users,Avg Duration,Bounce Rate,Engagement Score\n";
    reportData.topPagesAnalysis.forEach(page => {
      csvContent += `"${page.pagePath}",${page.pageViews},${page.users},${page.avgDuration},${page.bounceRate},${page.engagementScore}\n`;
    });
    
    csvContent += "\nSEO RECOMMENDATIONS\n";
    csvContent += "Priority,Issue,Type\n";
    reportData.seoRecommendations.forEach(rec => {
      csvContent += `${rec.priority},"${rec.issue}",${rec.type}\n`;
    });

    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lumibears-seo-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('SEO report exported as CSV!');
  };

  const exportRawDataCSV = () => {
    const reportData = generateSEOReport();
    
    let csvContent = "LUMIBEARS RAW GOOGLE ANALYTICS DATA EXPORT\n";
    csvContent += `Generated: ${reportData.reportGeneratedTimestamp}\n`;
    csvContent += `Property ID: ${reportData.googleAnalyticsPropertyId}\n`;
    csvContent += `Records: ${reportData.dataMetadata.organicTrafficRecordCount} organic + ${reportData.dataMetadata.topPagesRecordCount} pages\n\n`;
    
    // Raw Organic Traffic Data - Every single record
    csvContent += "=== RAW ORGANIC TRAFFIC DATA (EVERY RECORD) ===\n";
    if (reportData.rawData.allOrganicPages.length > 0) {
      const headers = Object.keys(reportData.rawData.allOrganicPages[0]);
      csvContent += headers.join(',') + '\n';
      
      reportData.rawData.allOrganicPages.forEach(record => {
        const values = headers.map(header => {
          const value = record[header];
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value || '';
        });
        csvContent += values.join(',') + '\n';
      });
    }
    
    csvContent += "\n=== RAW TOP PAGES DATA (EVERY RECORD) ===\n";
    if (reportData.rawData.allTopPages.length > 0) {
      const headers = Object.keys(reportData.rawData.allTopPages[0]);
      csvContent += headers.join(',') + '\n';
      
      reportData.rawData.allTopPages.forEach(record => {
        const values = headers.map(header => {
          const value = record[header];
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value || '';
        });
        csvContent += values.join(',') + '\n';
      });
    }
    
    // Metadata
    csvContent += "\n=== DATA METADATA ===\n";
    csvContent += "Field,Value\n";
    Object.entries(reportData.dataMetadata).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        csvContent += `${key},"${value.join(', ')}"\n`;
      } else {
        csvContent += `${key},"${value}"\n`;
      }
    });

    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lumibears-raw-analytics-data-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('Raw analytics data exported! This contains every individual record from Google Analytics.');
  };

  const exportToPDF = () => {
    const reportData = generateSEOReport();
    
    // Generate comprehensive HTML report for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>LumiBears SEO Analysis Report</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #2c3e50; margin: 40px; }
          .header { text-align: center; border-bottom: 3px solid #667eea; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #667eea; margin-bottom: 10px; }
          .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 20px 0; }
          .metric-card { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; border-left: 4px solid #667eea; }
          .metric-value { font-size: 24px; font-weight: bold; color: #667eea; }
          .metric-label { font-size: 14px; color: #7f8c8d; text-transform: uppercase; }
          .section { margin: 30px 0; }
          .section h2 { color: #2c3e50; border-bottom: 2px solid #ecf0f1; padding-bottom: 10px; }
          .section h3 { color: #34495e; margin-top: 25px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ecf0f1; }
          th { background-color: #f8f9fa; font-weight: 600; color: #2c3e50; }
          .priority-high { color: #e74c3c; font-weight: bold; }
          .priority-medium { color: #f39c12; font-weight: bold; }
          .priority-low { color: #27ae60; font-weight: bold; }
          .insight-card { background: #f8f9fa; padding: 15px; margin: 10px 0; border-radius: 8px; border-left: 4px solid #3498db; }
          .next-steps { background: #e8f5e8; padding: 20px; border-radius: 8px; border-left: 4px solid #27ae60; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ecf0f1; color: #7f8c8d; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🐻 LumiBears SEO Analysis Report</h1>
          <p>Generated on ${reportData.reportGenerated} | ${reportData.timeframe}</p>
        </div>

        <div class="section">
          <h2>📊 Executive Summary</h2>
          <div class="summary-grid">
            <div class="metric-card">
              <div class="metric-value">${reportData.summary.totalOrganicSessions}</div>
              <div class="metric-label">Organic Sessions</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${reportData.summary.averageBounceRate}</div>
              <div class="metric-label">Avg Bounce Rate</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${reportData.summary.totalRecommendations}</div>
              <div class="metric-label">SEO Issues</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${reportData.summary.topPagesAnalyzed}</div>
              <div class="metric-label">Pages Analyzed</div>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>🔍 Key Findings</h2>
          ${reportData.keyFindings.topPerformingPage ? `
            <h3>Top Performing Page</h3>
            <p><strong>Page:</strong> ${reportData.keyFindings.topPerformingPage.path}</p>
            <p><strong>Views:</strong> ${reportData.keyFindings.topPerformingPage.views} | <strong>Users:</strong> ${reportData.keyFindings.topPerformingPage.users}</p>
          ` : ''}
          
          ${reportData.keyFindings.bestOrganicLandingPage ? `
            <h3>Best Organic Landing Page</h3>
            <p><strong>Page:</strong> ${reportData.keyFindings.bestOrganicLandingPage.page}</p>
            <p><strong>Sessions:</strong> ${reportData.keyFindings.bestOrganicLandingPage.sessions} | <strong>Bounce Rate:</strong> ${reportData.keyFindings.bestOrganicLandingPage.bounceRate}</p>
          ` : ''}
          
          <h3>Average Session Duration</h3>
          <p>${reportData.keyFindings.averageSessionDuration}</p>
        </div>

        ${reportData.seoRecommendations.length > 0 ? `
        <div class="section">
          <h2>⚠️ SEO Recommendations</h2>
          <table>
            <thead>
              <tr><th>Priority</th><th>Issue</th><th>Type</th></tr>
            </thead>
            <tbody>
              ${reportData.seoRecommendations.map(rec => `
                <tr>
                  <td class="priority-${rec.priority}">${rec.priority.toUpperCase()}</td>
                  <td>${rec.issue}</td>
                  <td>${rec.type}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        <div class="section">
          <h2>🚀 Actionable Insights</h2>
          ${reportData.actionableInsights.map(insight => `
            <div class="insight-card">
              <h3>${insight.category}</h3>
              <p><strong>Finding:</strong> ${insight.insight}</p>
              <p><strong>Recommendation:</strong> ${insight.recommendation}</p>
              <p><strong>Priority:</strong> <span class="priority-${insight.priority.toLowerCase()}">${insight.priority}</span></p>
              <p><strong>Estimated Impact:</strong> ${insight.estimatedImpact}</p>
            </div>
          `).join('')}
        </div>

        <div class="section">
          <h2>📈 Top Organic Traffic Pages</h2>
          <table>
            <thead>
              <tr><th>Landing Page</th><th>Sessions</th><th>Users</th><th>Bounce Rate</th><th>SEO Score</th></tr>
            </thead>
            <tbody>
              ${reportData.organicTrafficAnalysis.slice(0, 10).map(page => `
                <tr>
                  <td>${page.landingPage}</td>
                  <td>${page.sessions}</td>
                  <td>${page.users}</td>
                  <td>${page.bounceRate}</td>
                  <td>${page.seoScore}/100</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <h2>📋 Next Steps Timeline</h2>
          ${reportData.nextSteps.map(step => `
            <div class="next-steps">
              <h3>${step.timeframe}</h3>
              <ul>
                ${step.actions.map(action => `<li>${action}</li>`).join('')}
              </ul>
            </div>
          `).join('')}
        </div>

        <div class="footer">
          <p>Report generated by LumiBears SEO Analytics Dashboard</p>
          <p>For questions about this report, please review the dashboard at /seo-analytics</p>
        </div>
      </body>
      </html>
    `;

    // Open in new window for printing/PDF save
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Trigger print dialog
    setTimeout(() => {
      printWindow.print();
    }, 500);
    
    message.success('SEO report opened for PDF export! Use your browser\'s print function to save as PDF.');
  };

  const organicTrafficColumns = [
    {
      title: 'Landing Page',
      dataIndex: 'landingPage',
      key: 'landingPage',
      width: '40%',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
      sorter: (a, b) => parseInt(a.sessions) - parseInt(b.sessions),
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Users',
      dataIndex: 'totalUsers',
      key: 'totalUsers',
      sorter: (a, b) => parseInt(a.totalUsers) - parseInt(b.totalUsers)
    },
    {
      title: 'Bounce Rate',
      dataIndex: 'bounceRate',
      key: 'bounceRate',
      render: (text) => {
        const rate = parseFloat(text);
        const color = rate > 70 ? 'red' : rate > 50 ? 'orange' : 'green';
        return <Text style={{ color }}>{rate.toFixed(1)}%</Text>;
      }
    }
  ];

  const topPagesColumns = [
    {
      title: 'Page Path',
      dataIndex: 'pagePath',
      key: 'pagePath',
      width: '50%',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Page Views',
      dataIndex: 'screenPageViews',
      key: 'screenPageViews',
      sorter: (a, b) => parseInt(a.screenPageViews) - parseInt(b.screenPageViews),
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Users',
      dataIndex: 'totalUsers',
      key: 'totalUsers',
      sorter: (a, b) => parseInt(a.totalUsers) - parseInt(b.totalUsers)
    },
    {
      title: 'Avg. Duration',
      dataIndex: 'averageSessionDuration',
      key: 'averageSessionDuration',
      render: (text) => {
        const seconds = parseFloat(text);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return <Text>{minutes}:{remainingSeconds.toString().padStart(2, '0')}</Text>;
      }
    }
  ];

  if (loading) {
    return (
      <Card style={{ textAlign: 'center', margin: '20px 0' }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: '16px' }}>Loading Google Analytics data...</Paragraph>
      </Card>
    );
  }

  if (!isAuthenticated) {
    return (
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GoogleOutlined />
            <span>Connect Google Analytics</span>
          </div>
        }
        style={{ margin: '20px 0' }}
      >
        <div style={{ textAlign: 'center' }}>
          <Paragraph>
            Connect your Google Analytics account to get detailed SEO insights and performance data.
          </Paragraph>
          <Paragraph type="secondary">
            This will help analyze your organic search traffic, top-performing pages, and identify SEO improvement opportunities.
          </Paragraph>
          
          {error && (
            <Alert 
              type="error" 
              message={error} 
              style={{ marginBottom: '16px' }}
              closable
              onClose={() => setError(null)}
            />
          )}
          
          <Button
            type="primary"
            size="large"
            icon={<GoogleOutlined />}
            onClick={handleAuthentication}
            disabled={!authUrl}
            style={{ background: '#667eea', borderColor: '#667eea' }}
          >
            Connect Google Analytics
          </Button>
          
          <div style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
            <Text type="secondary">
              You'll be redirected to Google to authorize access to your Analytics data.
              We only request read-only access to generate SEO insights.
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ margin: '20px 0' }}>
      {/* Header */}
      <Card style={{ marginBottom: '20px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LineChartOutlined />
              SEO Analytics Dashboard
            </Title>
          </Col>
          <Col>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button.Group>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={exportToJSON}
                  title="Export complete data structure as JSON"
                >
                  JSON
                </Button>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={exportRawDataCSV}
                  title="Export lowest-level raw analytics data as CSV"
                  type="primary"
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                  Raw Data CSV
                </Button>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={exportToCSV}
                  title="Export summary analysis as CSV"
                >
                  Summary CSV
                </Button>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={exportToPDF}
                  title="Export comprehensive report as PDF"
                  style={{ background: '#667eea', borderColor: '#667eea', color: 'white' }}
                >
                  PDF Report
                </Button>
              </Button.Group>
              <Button onClick={handleSignOut}>Sign Out</Button>
            </div>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert 
          type="error" 
          message={error} 
          style={{ marginBottom: '20px' }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* SEO Insights Summary */}
      {insights && (
        <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <TrophyOutlined style={{ fontSize: '24px', color: '#667eea' }} />
                <Title level={4} style={{ margin: '8px 0' }}>
                  {insights.organicTraffic.totalSessions}
                </Title>
                <Text type="secondary">Organic Sessions (30 days)</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <LineChartOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                <Title level={4} style={{ margin: '8px 0' }}>
                  {insights.organicTraffic.averageBounceRate}%
                </Title>
                <Text type="secondary">Avg. Bounce Rate</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <WarningOutlined style={{ fontSize: '24px', color: '#ff7875' }} />
                <Title level={4} style={{ margin: '8px 0' }}>
                  {insights.recommendations.length}
                </Title>
                <Text type="secondary">SEO Recommendations</Text>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* SEO Recommendations */}
      {insights?.recommendations?.length > 0 && (
        <Card title="SEO Recommendations" style={{ marginBottom: '20px' }}>
          {insights.recommendations.map((rec, index) => (
            <Alert
              key={index}
              type={rec.priority === 'high' ? 'error' : 'warning'}
              message={rec.message}
              style={{ marginBottom: '8px' }}
              showIcon
            />
          ))}
        </Card>
      )}

      {/* Organic Traffic Data */}
      {analyticsData?.organicTraffic && (
        <Card title="Organic Search Traffic" style={{ marginBottom: '20px' }}>
          <Table
            dataSource={analyticsData.organicTraffic}
            columns={organicTrafficColumns}
            pagination={{ pageSize: 10 }}
            rowKey={(record, index) => index}
            scroll={{ x: true }}
          />
        </Card>
      )}

      {/* Top Pages */}
      {analyticsData?.topPages && (
        <Card title="Top Performing Pages">
          <Table
            dataSource={analyticsData.topPages}
            columns={topPagesColumns}
            pagination={{ pageSize: 10 }}
            rowKey={(record, index) => index}
            scroll={{ x: true }}
          />
        </Card>
      )}
    </div>
  );
};

export default GoogleAnalyticsDashboard;